import { useCallback, useState } from 'react';

interface DependencyModeState {
  hoveredInitiativeId: string | null;
  isDependencyMode: boolean;
  selectedInitiativeId: string | null;
}

interface UseDependencyModeReturn {
  dependencyState: DependencyModeState;
  isInitiativeRelated: (
    id: string,
    dependencies: { masterNodeId: number; slaveNodeId: number }[]
  ) => boolean;
  resetDependencySelection: () => void;
  selectInitiative: (id: string | null) => void;
  setHoveredInitiative: (id: string | null) => void;
  toggleDependencyMode: (enabled: boolean) => void;
}

const useDependencyMode = (): UseDependencyModeReturn => {
  const [dependencyState, setDependencyState] = useState<DependencyModeState>({
    isDependencyMode: false,
    selectedInitiativeId: null,
    hoveredInitiativeId: null,
  });

  const toggleDependencyMode = useCallback((enabled: boolean) => {
    setDependencyState(prev => {
      const newState = {
        ...prev,
        isDependencyMode: enabled,
        selectedInitiativeId: enabled ? prev.selectedInitiativeId : null,
        hoveredInitiativeId: null,
      };
      return newState;
    });
  }, []);

  const selectInitiative = useCallback((id: string | null) => {
    setDependencyState(prev => {
      const newState = {
        ...prev,
        selectedInitiativeId: prev.selectedInitiativeId === id ? null : id,
        hoveredInitiativeId: null,
      };
      return newState;
    });
  }, []);

  const setHoveredInitiative = useCallback((id: string | null) => {
    setDependencyState(prev => ({
      ...prev,
      hoveredInitiativeId: id,
    }));
  }, []);

  const isInitiativeRelated = useCallback(
    (id: string, dependencies: { masterNodeId: number; slaveNodeId: number }[]): boolean => {
      if (!dependencyState.selectedInitiativeId) return false;

      // Check if this initiative is the selected one
      if (id === dependencyState.selectedInitiativeId) return true;

      // Check if this initiative has dependencies from the selected one
      // The dependency relationship: masterNodeId (this depends on) -> slaveNodeId (this initiative)
      return dependencies.some(
        dep => dep.masterNodeId.toString() === dependencyState.selectedInitiativeId
      );
    },
    [dependencyState.selectedInitiativeId]
  );

  const resetDependencySelection = useCallback(() => {
    setDependencyState(prev => ({
      ...prev,
      selectedInitiativeId: null,
      hoveredInitiativeId: null,
    }));
  }, []);

  return {
    dependencyState,
    toggleDependencyMode,
    selectInitiative,
    setHoveredInitiative,
    isInitiativeRelated,
    resetDependencySelection,
  };
};

export { useDependencyMode };
export type { DependencyModeState, UseDependencyModeReturn };
